/**
 * Phase 27.31 — Public Lead Landing Page.
 *
 * Rendered at /connect/:slug. Loads its own data via the anonymous
 * `publicLandingPagesAPI` and submits directly into the Leads
 * pipeline via the same client. No auth required.
 *
 * Design goals (per spec section 29 + 14 + 28):
 *   - Mobile-first: two-thirds of visitors will scan a QR from a
 *     phone at an event.
 *   - Medicare-friendly: the "medicare" template bumps type sizes
 *     and contrast so 65+ audiences can read it comfortably.
 *   - Premium feel: hero + agent card + simple form, no aggressive
 *     marketing patterns (no fake urgency, no popups, no flash).
 */
import { useEffect, useMemo, useState } from 'react';
import { formatPhoneInput } from '@/lib/utils';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { publicLandingPagesAPI } from '../lib/api';
import { SERVICE_CATALOG } from '../lib/landingPageConstants';
import {
  t as tt, getStoredLanguage, setStoredLanguage, serviceLabel,
} from '../lib/landingPageI18n';
import { CheckCircle2, Phone, Mail, Shield, Loader2, MapPin, Award, Users, HeartHandshake, Lock, Building2,
  ShieldPlus, ClipboardList, Plane, Sparkles, HandHeart, HeartPulse, Globe, Calendar, Clock } from 'lucide-react';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import { flashAutofilled } from '../lib/flashAutofilled';
import PublicChatWidget, { attachLeadToChatSession } from '../components/public/PublicChatWidget';

// ── template variants ────────────────────────────────────────────────
// Each variant is just a tokens dict — kept flat so the JSX stays
// readable and CSS classes are colocated with their tokens.
const TEMPLATES = {
  professional: {
    heroBg: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
    heroText: 'text-slate-100',
    accent: 'text-sky-400',
    accentBg: 'bg-sky-600 hover:bg-sky-700 focus-visible:ring-sky-500',
    body: 'bg-white text-slate-900',
    card: 'bg-white border-slate-200 shadow-sm',
    label: 'text-slate-700',
    input: 'border-slate-300 focus-visible:border-sky-500 focus-visible:ring-sky-500',
    fontScale: 'text-base',
    headlineSize: 'text-3xl sm:text-4xl lg:text-5xl',
    highlightBg: 'bg-sky-50 text-sky-800 border-sky-200',
  },
  medicare: {
    heroBg: 'bg-gradient-to-br from-blue-900 via-indigo-900 to-blue-900',
    heroText: 'text-white',
    accent: 'text-amber-300',
    accentBg: 'bg-amber-500 hover:bg-amber-600 focus-visible:ring-amber-500 text-slate-900',
    body: 'bg-blue-50/40 text-slate-900',
    card: 'bg-white border-blue-200 shadow-md',
    label: 'text-slate-800 font-semibold',
    input: 'border-slate-400 focus-visible:border-blue-600 focus-visible:ring-blue-600',
    // Medicare-friendly: bumped body size + larger form controls.
    fontScale: 'text-lg',
    headlineSize: 'text-4xl sm:text-5xl lg:text-6xl',
    highlightBg: 'bg-amber-100 text-amber-900 border-amber-300',
  },
  family_health: {
    heroBg: 'bg-gradient-to-br from-emerald-800 via-teal-800 to-cyan-800',
    heroText: 'text-white',
    accent: 'text-emerald-300',
    accentBg: 'bg-emerald-600 hover:bg-emerald-700 focus-visible:ring-emerald-500',
    body: 'bg-emerald-50/40 text-slate-900',
    card: 'bg-white border-emerald-200 shadow-sm',
    label: 'text-slate-700',
    input: 'border-slate-300 focus-visible:border-emerald-600 focus-visible:ring-emerald-500',
    fontScale: 'text-base',
    headlineSize: 'text-3xl sm:text-4xl lg:text-5xl',
    highlightBg: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  },
  // Editorial black with rose accents. Contemporary and clean —
  // pairs well with younger consumer audiences (30-55) shopping
  // for individual/family health or life products.
  modern_minimal: {
    heroBg: 'bg-gradient-to-br from-neutral-900 via-zinc-900 to-black',
    heroText: 'text-neutral-100',
    accent: 'text-rose-400',
    accentBg: 'bg-rose-500 hover:bg-rose-600 focus-visible:ring-rose-500',
    body: 'bg-neutral-50 text-neutral-900',
    card: 'bg-white border-neutral-200 shadow-sm',
    label: 'text-neutral-700',
    input: 'border-neutral-300 focus-visible:border-rose-500 focus-visible:ring-rose-500',
    fontScale: 'text-base',
    headlineSize: 'text-3xl sm:text-4xl lg:text-5xl',
    highlightBg: 'bg-rose-50 text-rose-800 border-rose-200',
  },
  // Charcoal with champagne gold — luxury retirement/annuity vibe.
  // Designed to signal experience, discretion, and high-net-worth.
  elegant_dark: {
    heroBg: 'bg-gradient-to-br from-stone-900 via-zinc-950 to-stone-900',
    heroText: 'text-amber-50',
    accent: 'text-amber-400',
    accentBg: 'bg-amber-500 hover:bg-amber-600 focus-visible:ring-amber-500 text-stone-900',
    body: 'bg-stone-50 text-stone-900',
    card: 'bg-white border-stone-200 shadow-sm',
    label: 'text-stone-700',
    input: 'border-stone-300 focus-visible:border-amber-500 focus-visible:ring-amber-500',
    fontScale: 'text-base',
    headlineSize: 'text-3xl sm:text-4xl lg:text-5xl',
    highlightBg: 'bg-amber-50 text-amber-900 border-amber-200',
  },
  // Warm terracotta gradient with amber accents — designed for the
  // Hispanic-community outreach we serve in Florida markets.
  warm_latino: {
    heroBg: 'bg-gradient-to-br from-orange-900 via-red-900 to-rose-900',
    heroText: 'text-orange-50',
    accent: 'text-orange-300',
    accentBg: 'bg-orange-500 hover:bg-orange-600 focus-visible:ring-orange-500',
    body: 'bg-orange-50/50 text-slate-900',
    card: 'bg-white border-orange-200 shadow-sm',
    label: 'text-slate-700',
    input: 'border-slate-300 focus-visible:border-orange-500 focus-visible:ring-orange-500',
    fontScale: 'text-base',
    headlineSize: 'text-3xl sm:text-4xl lg:text-5xl',
    highlightBg: 'bg-orange-50 text-orange-800 border-orange-200',
  },
  // Cheerful rose-to-orange gradient with sunny yellow accents.
  // Built for dental, vision, and wellness campaigns where the tone
  // should feel bright and approachable.
  wellness_bright: {
    heroBg: 'bg-gradient-to-br from-rose-800 via-pink-800 to-orange-800',
    heroText: 'text-yellow-50',
    accent: 'text-yellow-300',
    accentBg: 'bg-yellow-400 hover:bg-yellow-500 focus-visible:ring-yellow-500 text-slate-900',
    body: 'bg-pink-50/40 text-slate-900',
    card: 'bg-white border-pink-200 shadow-sm',
    label: 'text-slate-700',
    input: 'border-slate-300 focus-visible:border-pink-500 focus-visible:ring-pink-500',
    fontScale: 'text-base',
    headlineSize: 'text-3xl sm:text-4xl lg:text-5xl',
    highlightBg: 'bg-yellow-50 text-yellow-900 border-yellow-200',
  },
  ocean_blue: {
    heroBg: 'bg-gradient-to-br from-cyan-900 via-sky-900 to-blue-900',
    heroText: 'text-cyan-50',
    accent: 'text-cyan-300',
    accentBg: 'bg-cyan-600 hover:bg-cyan-700 focus-visible:ring-cyan-500',
    body: 'bg-cyan-50/40 text-slate-900',
    card: 'bg-white border-cyan-200 shadow-sm',
    label: 'text-slate-700',
    input: 'border-slate-300 focus-visible:border-cyan-500 focus-visible:ring-cyan-500',
    fontScale: 'text-base',
    headlineSize: 'text-3xl sm:text-4xl lg:text-5xl',
    highlightBg: 'bg-cyan-50 text-cyan-800 border-cyan-200',
  },
  royal_purple: {
    heroBg: 'bg-gradient-to-br from-violet-950 via-purple-900 to-indigo-950',
    heroText: 'text-violet-50',
    accent: 'text-violet-300',
    accentBg: 'bg-violet-600 hover:bg-violet-700 focus-visible:ring-violet-500',
    body: 'bg-violet-50/40 text-slate-900',
    card: 'bg-white border-violet-200 shadow-sm',
    label: 'text-slate-700',
    input: 'border-slate-300 focus-visible:border-violet-500 focus-visible:ring-violet-500',
    fontScale: 'text-base',
    headlineSize: 'text-3xl sm:text-4xl lg:text-5xl',
    highlightBg: 'bg-violet-50 text-violet-800 border-violet-200',
  },
  crimson_red: {
    heroBg: 'bg-gradient-to-br from-red-950 via-rose-900 to-red-950',
    heroText: 'text-rose-50',
    accent: 'text-rose-300',
    accentBg: 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500',
    body: 'bg-rose-50/40 text-slate-900',
    card: 'bg-white border-rose-200 shadow-sm',
    label: 'text-slate-700',
    input: 'border-slate-300 focus-visible:border-red-500 focus-visible:ring-red-500',
    fontScale: 'text-base',
    headlineSize: 'text-3xl sm:text-4xl lg:text-5xl',
    highlightBg: 'bg-rose-50 text-rose-800 border-rose-200',
  },
  forest_green: {
    heroBg: 'bg-gradient-to-br from-green-950 via-emerald-900 to-green-950',
    heroText: 'text-emerald-50',
    accent: 'text-emerald-300',
    accentBg: 'bg-green-600 hover:bg-green-700 focus-visible:ring-green-500',
    body: 'bg-green-50/40 text-slate-900',
    card: 'bg-white border-green-200 shadow-sm',
    label: 'text-slate-700',
    input: 'border-slate-300 focus-visible:border-green-500 focus-visible:ring-green-500',
    fontScale: 'text-base',
    headlineSize: 'text-3xl sm:text-4xl lg:text-5xl',
    highlightBg: 'bg-green-50 text-green-800 border-green-200',
  },
  sunset_coral: {
    heroBg: 'bg-gradient-to-br from-rose-900 via-orange-800 to-amber-800',
    heroText: 'text-amber-50',
    accent: 'text-amber-200',
    accentBg: 'bg-rose-500 hover:bg-rose-600 focus-visible:ring-rose-500',
    body: 'bg-orange-50/40 text-slate-900',
    card: 'bg-white border-orange-200 shadow-sm',
    label: 'text-slate-700',
    input: 'border-slate-300 focus-visible:border-orange-500 focus-visible:ring-orange-500',
    fontScale: 'text-base',
    headlineSize: 'text-3xl sm:text-4xl lg:text-5xl',
    highlightBg: 'bg-orange-50 text-orange-800 border-orange-200',
  },
  midnight_gold: {
    heroBg: 'bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950',
    heroText: 'text-amber-50',
    accent: 'text-amber-300',
    accentBg: 'bg-amber-500 hover:bg-amber-600 focus-visible:ring-amber-500 text-slate-900',
    body: 'bg-slate-50 text-slate-900',
    card: 'bg-white border-slate-200 shadow-sm',
    label: 'text-slate-700',
    input: 'border-slate-300 focus-visible:border-amber-500 focus-visible:ring-amber-500',
    fontScale: 'text-base',
    headlineSize: 'text-3xl sm:text-4xl lg:text-5xl',
    highlightBg: 'bg-amber-50 text-amber-900 border-amber-200',
  },
};

// Resolve a landing page's photo URL to a full HTTP URL by prefixing
// REACT_APP_BACKEND_URL when we get a relative path (which is what
// the API returns for both the marketing photo and the fallback
// profile photo).
function absoluteUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return `${base}${url}`;
}

// Icons paired with the SERVICE_CATALOG keys. Kept here (not in
// landingPageConstants) so the constants file stays lucide-free and
// safe to import from server-rendered helpers.
const SERVICE_ICONS = {
  medicare: Shield,
  individual_family_health: Users,
  life: HeartHandshake,
  dental_vision: Sparkles,
  supplemental: ShieldPlus,
  cancer_critical_illness: HeartPulse,
  annuities_retirement: Award,
  travel: Plane,
  other: ClipboardList,
};

// Trust indicators shown below the hero — three curated highlights
// that mirror the mock. Kept generic (no product-specific claims)
// so every template can render them.
const TRUST_HIGHLIGHTS = [
  { icon: HandHeart, title: 'Personalized Guidance',
    body: 'I take the time to understand your needs and explain your options clearly.' },
  { icon: Building2, title: 'Multiple Top Carriers',
    body: 'Access to a wide range of trusted insurance companies to find the best fit.' },
  { icon: HeartHandshake, title: 'Ongoing Support',
    body: "I'm here for you after enrollment to provide continued support." },
];

// Footer trust bar — signals licensure, locality, focus, privacy.
const FOOTER_BADGES = [
  { icon: Shield, title: 'Licensed & Experienced', body: 'Licensed insurance professional' },
  { icon: MapPin, title: 'Local & Independent', body: 'Local support with personalized service' },
  { icon: Award, title: 'Client Focused', body: 'Your goals and peace of mind come first' },
  { icon: Lock, title: 'Your Info is Protected', body: 'We respect your privacy and keep your data safe' },
];

function AgentPhoto({ page, tokens }) {
  const [failed, setFailed] = useState(false);
  const mode = page.agent_display?.photo_mode || 'profile';
  const url = useMemo(() => {
    if (mode === 'none') return null;
    if (mode === 'custom') return absoluteUrl(page.agent_display?.marketing_photo_url);
    return absoluteUrl(page._agent_profile_photo_url);
  }, [mode, page]);

  const initials = (page.agent_display?.display_name || '')
    .split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  if (!url || failed) {
    return (
      <div className={`w-28 h-28 sm:w-32 sm:h-32 rounded-full flex items-center justify-center border-2 ${tokens.card} font-bold text-2xl`}>
        {initials || 'A'}
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={page.agent_display?.display_name}
      onError={() => setFailed(true)}
      className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-white/30 shadow-lg"
      data-testid="landing-agent-photo"
    />
  );
}

// Shared bilingual controls: first-visit language picker overlay +
// a floating top-right switcher. Rendered on EVERY layout (standard,
// simple, appointments-only, and team) so the visitor can always pick
// and change the language.
function LanguageControls({ show, onPick, onOpen, language, t }) {
  return (
    <>
      {show && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="lp-language-picker"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-5 h-5 text-sky-600" />
              <h3 className="text-lg font-heading font-semibold text-slate-900">
                {t('picker_title')}
              </h3>
            </div>
            <p className="text-sm text-slate-500 mb-5">{t('picker_subtitle')}</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => onPick('en')}
                className="rounded-xl border-2 border-slate-200 hover:border-sky-500 hover:bg-sky-50 px-4 py-4 text-left transition-colors"
                data-testid="lp-language-pick-en"
              >
                <div className="text-3xl mb-1">🇺🇸</div>
                <div className="font-semibold text-slate-900">English</div>
                <div className="text-xs text-slate-500 mt-0.5">Default</div>
              </button>
              <button
                type="button"
                onClick={() => onPick('es')}
                className="rounded-xl border-2 border-slate-200 hover:border-sky-500 hover:bg-sky-50 px-4 py-4 text-left transition-colors"
                data-testid="lp-language-pick-es"
              >
                <div className="text-3xl mb-1">🇪🇸</div>
                <div className="font-semibold text-slate-900">Español</div>
                <div className="text-xs text-slate-500 mt-0.5">Predeterminado</div>
              </button>
            </div>
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={onOpen}
        className="fixed top-3 right-3 z-40 inline-flex items-center gap-1.5 rounded-full bg-white/95 border border-slate-200 shadow-md px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-white hover:shadow-lg transition-all"
        data-testid="lp-language-switcher"
      >
        <Globe size={13} />
        {language === 'es' ? 'ES' : 'EN'}
        <span className="text-slate-400">·</span>
        <span className="text-slate-500 font-normal">{t('lang_switch_label')}</span>
      </button>
    </>
  );
}


export default function PublicLandingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Capture UTM params from the URL exactly once — even if the user
  // navigates within the page later, we submit whatever they arrived
  // with. This preserves campaign attribution for e.g. `?utm_source=
  // tampa_expo_2026&utm_medium=qr&utm_campaign=fall_medicare`.
  const [utm] = useState(() => {
    if (typeof window === 'undefined') return {};
    const q = new URLSearchParams(window.location.search);
    return {
      utm_source: q.get('utm_source') || undefined,
      utm_medium: q.get('utm_medium') || undefined,
      utm_campaign: q.get('utm_campaign') || undefined,
      utm_content: q.get('utm_content') || undefined,
      utm_term: q.get('utm_term') || undefined,
    };
  });
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '', email: '', zip_code: '',
    preferred_contact: '', message: '',
    services_interested: [], consent_accepted: false, sms_consent_accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Which action the visitor is completing right now. Toggles the
  // form column between "Get in touch" and "Schedule a call".
  // Defaults to 'info' (lower commitment) — the switcher only
  // renders when the landing page has appointments enabled.
  const [mode, setMode] = useState('info');
  // Fetched separately from `page` because it lives on a public
  // endpoint that skips authentication. Drives whether we render
  // the mode switcher + the booking widget.
  const [apptCfg, setApptCfg] = useState(null);
  // Per-field validation errors. Rendered as red text under the
  // corresponding input AND red-bordered inputs. Cleared as the
  // visitor edits the field so the UI feels forgiving.
  const [errors, setErrors] = useState({});
  // Phase 27.37.U — Kora chatbot session bookkeeping. Non-null after
  // the widget hands off qualified fields; used to link the conversation
  // to the lead upon successful form submit.
  const [chatSessionId, setChatSessionId] = useState(null);
  // Phase 27.37.W — When Kora auto-creates the lead directly from the
  // conversation, we hide the form and replace it with a confirmation
  // card so the visitor knows they don't need to fill anything else.
  const [chatAutoCreatedLead, setChatAutoCreatedLead] = useState(null);
  // Phase 27.40 — Bilingual landing page. On first visit we prompt for
  // language (EN default, ES). Choice persists in localStorage keyed on
  // the slug so returning visitors skip the picker. A floating button
  // in the top-right lets them flip languages any time.
  const [language, setLanguage] = useState('en');
  const [showLangPicker, setShowLangPicker] = useState(false);
  useEffect(() => {
    if (!slug) return;
    const stored = getStoredLanguage(slug);
    if (stored === 'en' || stored === 'es') {
      setLanguage(stored);
    } else {
      setShowLangPicker(true);
    }
  }, [slug]);
  const t = (k) => tt(k, language);
  const chooseLanguage = (lang) => {
    setLanguage(lang);
    setStoredLanguage(slug, lang);
    setShowLangPicker(false);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await publicLandingPagesAPI.get(slug);
        if (!mounted) return;
        setPage(r.data);
        // Pre-select the only available service so single-product
        // landing pages don't force an obvious extra tap.
        if ((r.data.services_enabled || []).length === 1) {
          setForm((f) => ({ ...f, services_interested: [...r.data.services_enabled] }));
        }
      } catch (e) {
        if (mounted) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  // Fetch appointment config in parallel — used to toggle the
  // A/B mode switcher and drive the booking widget's UI.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await publicLandingPagesAPI.getAppointmentConfig(slug);
        if (mounted) setApptCfg(r.data);
      } catch (e) {
        if (mounted) setApptCfg({ enabled: false });
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  // Lazily inject the Google Fonts stylesheet for the cursive tagline
  // ("Great Vibes") — only when this page actually uses it, and only
  // once per document. Skipping this download for pages that don't
  // need it keeps the bandwidth cost off Medicare-audience users on
  // slow mobile connections.
  useEffect(() => {
    if (!page?.content?.highlight_cursive || !page?.content?.show_highlight) return;
    const id = 'lp-cursive-font';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
    document.head.appendChild(link);
  }, [page?.content?.highlight_cursive, page?.content?.show_highlight]);

  const tokens = TEMPLATES[page?.template_style] || TEMPLATES.professional;
  // Simple/minimalist referral layout — hides marketing extras (service
  // picker + trust highlights) to keep a clean contact card. The lead
  // capture, consent and appointment logic are identical to standard.
  const isSimple = page?.layout === 'simple' || page?.layout === 'digital_card';
  // Appointments-only: hide the lead form entirely, show only the
  // booking widget (a per-agent "book a call" link).
  const isApptOnly = page?.layout === 'appointments_only';
  // Team page: KR roster where the visitor picks an agent, then books.
  const isTeam = page?.layout === 'team_appointments';

  useEffect(() => { if (isApptOnly) setMode('appointment'); }, [isApptOnly]);

  // Build the input className. Swaps the template's focus-ring for a
  // red border + red focus-ring when the field is currently errored,
  // so the error state is visible even before the user focuses.
  const inputClass = (hasError) => {
    const base = 'w-full rounded-lg border px-3 py-2.5 outline-none transition';
    if (hasError) {
      return `${base} border-red-500 focus-visible:border-red-500 focus-visible:ring-red-500 bg-red-50/30`;
    }
    return `${base} ${tokens.input}`;
  };

  const toggleService = (key) => {
    setForm((f) => ({
      ...f,
      services_interested: f.services_interested.includes(key)
        ? f.services_interested.filter((s) => s !== key)
        : [...f.services_interested, key],
    }));
  };

  // Update a form field and clear its inline error (if any).
  const updateField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  // Parse the various shapes FastAPI/Pydantic returns for validation
  // errors. 422 responses ship `detail` as an ARRAY of field errors
  // — trying to render that directly is what was surfacing the
  // generic "something went wrong" toast for users who left an
  // optional select empty.
  const readableError = (err) => {
    const d = err?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d) && d.length) {
      const first = d[0];
      const loc = Array.isArray(first?.loc) ? first.loc.filter((s) => s !== 'body').join('.') : '';
      const msg = first?.msg || 'Invalid value';
      return loc ? `${loc}: ${msg}` : msg;
    }
    if (d && typeof d === 'object') return d?.msg || 'Unable to submit — please try again.';
    return 'Unable to submit — please try again.';
  };

  // Map a 422 `detail` array back to per-field errors so the inline
  // messages match whatever the server complained about. Falls back
  // to a form-level toast when the loc is missing.
  const applyServerErrors = (err) => {
    const d = err?.response?.data?.detail;
    if (!Array.isArray(d)) return false;
    const next = {};
    for (const item of d) {
      const loc = Array.isArray(item?.loc) ? item.loc.filter((s) => s !== 'body') : [];
      const field = loc[loc.length - 1];
      if (field && typeof field === 'string') {
        next[field] = item.msg || 'Invalid value';
      }
    }
    if (Object.keys(next).length === 0) return false;
    setErrors((prev) => ({ ...prev, ...next }));
    return true;
  };

  // Build a per-field errors object based on the current form + the
  // page's form_config. Returns {} when everything is valid.
  const validate = () => {
    const cfg = page?.form_config || {};
    const next = {};
    if (!form.first_name.trim()) next.first_name = 'First name is required.';
    if (!form.last_name.trim()) next.last_name = 'Last name is required.';
    if (!form.phone.trim()) {
      next.phone = 'Phone number is required.';
    } else if (form.phone.replace(/\D/g, '').length < 7) {
      next.phone = 'Please enter a valid phone number.';
    }
    if (cfg.email === 'required' && !form.email.trim()) {
      next.email = 'Email is required.';
    } else if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = 'Please enter a valid email address.';
    }
    if (cfg.zip === 'required' && !form.zip_code.trim()) {
      next.zip_code = 'ZIP code is required.';
    } else if (form.zip_code.trim() && !/^\d{5}(-\d{4})?$/.test(form.zip_code.trim())) {
      next.zip_code = 'Please enter a valid 5-digit ZIP code.';
    }
    if (!form.consent_accepted) next.consent_accepted = 'Please accept the consent to continue.';
    return next;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (Object.keys(v).length) {
      setErrors(v);
      toast.error('Please fix the highlighted fields below.');
      // Scroll to the first errored field for accessibility on mobile.
      const firstKey = Object.keys(v)[0];
      const el = document.querySelector(`[data-testid="landing-${firstKey.replace(/_/g, '-')}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      // Sanitize the payload before submit:
      //   * `preferred_contact` is a Literal on the backend — sending
      //     "" (default select value) fails Pydantic validation with
      //     a 422 that used to surface as a generic error toast.
      //   * Trim other free-text fields so accidental whitespace
      //     doesn't slip into the CRM.
      const clean = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        zip_code: form.zip_code.trim() || undefined,
        preferred_contact: form.preferred_contact || undefined,
        message: form.message.trim() || undefined,
        services_interested: form.services_interested,
        consent_accepted: form.consent_accepted,
        sms_consent_accepted: form.sms_consent_accepted,
        sms_consent_language: language,
        preferred_language: language,
      };
      const submitResp = await publicLandingPagesAPI.submit(slug, { ...clean, ...utm });
      // If the chatbot conducted a pre-form conversation, attach the
      // transcript to the freshly-created lead so the agent sees it.
      const leadId = submitResp?.data?.lead_id;
      if (chatSessionId && leadId) {
        attachLeadToChatSession(chatSessionId, leadId);
      }
      setSubmitted(true);
    } catch (err) {
      // Try to hoist server-side field errors onto the inline UI so
      // the visitor sees exactly what to fix (not just a toast).
      const hadFieldErrors = applyServerErrors(err);
      toast.error(hadFieldErrors ? 'Please fix the highlighted fields below.' : readableError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (notFound || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-slate-900">Page not available</h1>
          <p className="mt-2 text-slate-600">
            This lead intake page is not currently accepting submissions.
            Please contact us directly at{' '}
            <a href="mailto:info@krenterprisegroup.com" className="text-sky-600 hover:underline">
              info@krenterprisegroup.com
            </a>.
          </p>
        </div>
      </div>
    );
  }

  const enabledServices = SERVICE_CATALOG.filter((s) => (page.services_enabled || []).includes(s.key));
  const rawContent = page.content || {};
  // Phase 27.41 — Bilingual authoring. Pick the ES override when the
  // visitor selected Spanish AND the admin actually filled that field
  // in. Otherwise fall back to the English copy so a partial
  // translation never leaves a blank hero.
  const pick = (en, es) => (language === 'es' && (es || '').trim() ? es : en);
  const content = {
    ...rawContent,
    headline: pick(rawContent.headline, rawContent.headline_es),
    supporting_text: pick(rawContent.supporting_text, rawContent.supporting_text_es),
    highlight_message: pick(rawContent.highlight_message, rawContent.highlight_message_es),
    cta_text: pick(rawContent.cta_text, rawContent.cta_text_es),
  };
  const brand = page.agency_brand || {};
  const cfg = page.form_config || {};
  // Bilingual consent — falls back to English text when the admin has
  // not published a Spanish version yet.
  const consentText = (language === 'es' && (page.consent?.text_es || '').trim())
    ? page.consent.text_es
    : page.consent?.text;
  // CTA label — admin override first, then dictionary default.
  const ctaLabel = (content.cta_text || '').trim()
    || (language === 'es' ? 'Solicitar Información' : 'Request Information');
  const submittingLabel = language === 'es' ? 'Enviando…' : 'Submitting…';

  // Team scheduler page (KR-owned roster) — its own layout.
  if (isTeam) {
    return (
      <>
        <LanguageControls
          show={showLangPicker}
          onPick={chooseLanguage}
          onOpen={() => setShowLangPicker(true)}
          language={language}
          t={t}
        />
        <TeamRosterView
          slug={slug}
          tokens={tokens}
          content={content}
          brand={brand}
          page={page}
          language={language}
        />
      </>
    );
  }

  return (
    <div className={`min-h-screen ${tokens.body} ${tokens.fontScale} relative`} data-testid="public-landing-page">
      {/* Phase 27.40 — First-visit language picker + floating switcher. */}
      <LanguageControls
        show={showLangPicker}
        onPick={chooseLanguage}
        onOpen={() => setShowLangPicker(true)}
        language={language}
        t={t}
      />
      {/* Top bar with brand + "Licensed Insurance Agent" pill,
          borrowed from the reference mock. Stays fixed-position feel
          via a bg-white strip so the dark hero underneath pops. */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between">
          {brand.show && (
            <div className="flex items-center gap-3">
              {brand.logo_url ? (
                <img
                  src={absoluteUrl(brand.logo_url)}
                  alt={`${brand.name || 'Agency'} logo`}
                  className="w-9 h-9 rounded object-contain bg-white"
                  data-testid="landing-header-agency-logo"
                />
              ) : (
                <div className="w-9 h-9 rounded bg-slate-900 text-white flex items-center justify-center font-black text-sm">KR</div>
              )}
              <div className="leading-tight">
                <div className="text-xs sm:text-sm font-bold text-slate-900 tracking-wide">{brand.name}</div>
                {brand.powered_by_kr ? (
                  <div className="text-[10px] italic text-slate-500" data-testid="landing-header-powered-by-kr">
                    Powered by KR Enterprise Group
                  </div>
                ) : (
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Insurance Solutions</div>
                )}
              </div>
            </div>
          )}
          <div className="hidden sm:flex items-start gap-2 text-slate-700">
            <Shield size={16} className="mt-0.5 text-slate-500 flex-shrink-0" />
            <div className="text-xs leading-tight">
              <div className="font-semibold">{page.agent_display?.professional_title}</div>
              <div className="text-slate-500">Here to help you and your family protect what matters most.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero + Form — two columns on lg+, stacked on mobile. */}
      <section className={`${tokens.heroBg} ${tokens.heroText} relative`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 lg:py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Agent column */}
          <div>
            <h1 className={`${tokens.headlineSize} font-black leading-[1.05]`} data-testid="landing-headline">
              {(() => {
                // Emphasize the final line of the headline in the
                // template accent color, like the reference. Falls
                // back gracefully for single-line headlines.
                const lines = (content.headline || '').split(/\n|(?<=\.)\s+/);
                if (lines.length < 2) return content.headline;
                return (
                  <>
                    {lines.slice(0, -1).join(' ')}{' '}
                    <span className={tokens.accent}>{lines[lines.length - 1]}</span>
                  </>
                );
              })()}
            </h1>
            {content.supporting_text && (
              <p className="mt-4 opacity-90 leading-relaxed max-w-lg">
                {content.supporting_text}
              </p>
            )}

            {/* Event / seminar details banner — shown on the "event"
                model when any detail is filled. The form on the right
                acts as the RSVP. */}
            {page.layout === 'event' && (content.event_date || content.event_time || content.event_location) && (
              <div className="mt-5 rounded-xl border border-white/20 bg-white/10 backdrop-blur-sm p-4 max-w-lg space-y-2"
                data-testid="landing-event-banner">
                {content.event_date && (
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Calendar size={16} className={tokens.accent} /> {content.event_date}
                    {content.event_time && <span className="opacity-90 font-normal">· {content.event_time}</span>}
                  </div>
                )}
                {content.event_time && !content.event_date && (
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Clock size={16} className={tokens.accent} /> {content.event_time}
                  </div>
                )}
                {(content.event_location || content.event_address) && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin size={16} className={`${tokens.accent} mt-0.5`} />
                    <span>
                      {content.event_location && <span className="font-semibold">{content.event_location}</span>}
                      {content.event_location && content.event_address && <br />}
                      {content.event_address && <span className="opacity-90">{content.event_address}</span>}
                    </span>
                  </div>
                )}
              </div>
            )}
            {/* Agent card — photo, name, title, contact rows.
                On mobile the whole block is centered (photo above,
                info below, highlight below that) so we don't leave
                an awkward right-side gap. On sm+ we revert to the
                two-column left-aligned layout. */}
            <div className="mt-8 flex flex-col sm:flex-row gap-5 items-center sm:items-start text-center sm:text-left">
              <AgentPhoto page={page} tokens={tokens} />
              <div className="flex-1 min-w-0 w-full sm:w-auto">
                <div className="text-2xl font-bold" data-testid="landing-agent-name">
                  {page.agent_display?.display_name}
                </div>
                <div className={`text-sm ${tokens.accent} font-medium`}>
                  {page.agent_display?.professional_title}
                </div>
                <div className="mt-3 space-y-1.5 text-sm opacity-90">
                  {page.agent_display?.phone && (
                    <a href={`tel:${page.agent_display.phone}`} className="flex items-center gap-2 hover:underline justify-center sm:justify-start">
                      <Phone size={14} className="opacity-70" /> {page.agent_display.phone}
                    </a>
                  )}
                  {page.agent_display?.email && (
                    <a href={`mailto:${page.agent_display.email}`} className="flex items-center gap-2 hover:underline break-all justify-center sm:justify-start">
                      <Mail size={14} className="opacity-70" /> {page.agent_display.email}
                    </a>
                  )}
                </div>
                {content.show_highlight && content.highlight_message && (
                  content.highlight_cursive ? (
                    <div
                      className="mt-4 text-3xl sm:text-4xl leading-none"
                      style={{
                        fontFamily: '"Great Vibes","Dancing Script","Brush Script MT",cursive',
                      }}
                      data-testid="landing-highlight"
                    >
                      <span className={tokens.accent}>{content.highlight_message}</span>
                    </div>
                  ) : (
                    <div className={`mt-4 inline-block px-3 py-1 rounded-full border ${tokens.highlightBg} font-medium text-xs`}
                      data-testid="landing-highlight">
                      {content.highlight_message}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Form column */}
          <div>
            {submitted ? (
              <div className={`rounded-2xl p-8 text-center border ${tokens.card} text-slate-800`}
                data-testid="landing-success">
                <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500" />
                <h2 className="mt-4 text-2xl font-bold">{t('thank_you')}</h2>
                <p className="mt-2 text-slate-600">
                  {chatAutoCreatedLead
                    ? (language === 'es'
                        ? `Recibimos tu información a través de Kora. ${page.agent_display?.display_name || 'El agente'} te contactará en breve.`
                        : `We received your info through Kora. ${page.agent_display?.display_name || 'The agent'} will contact you shortly.`)
                    : (language === 'es'
                        ? `${page.agent_display?.display_name || 'El agente'} te contactará en breve sobre tu solicitud.`
                        : `${page.agent_display?.display_name || 'The agent'} will contact you shortly regarding your request.`)}
                </p>
                {chatAutoCreatedLead && apptCfg?.enabled && apptCfg?.has_windows && (
                  <button
                    type="button"
                    onClick={() => { setChatAutoCreatedLead(null); setSubmitted(false); setMode('appointment'); }}
                    className="mt-5 inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2.5 text-sm font-semibold hover:bg-slate-800"
                    data-testid="landing-schedule-instead-btn"
                  >
                    {language === 'es' ? '¿Prefieres agendar una cita?' : 'Prefer to schedule a call?'}
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* A/B mode switcher — only renders when appointments
                    are enabled on the landing page. Visitor picks
                    either "Get in touch" (default) or "Book an
                    appointment", never both at once. */}
                {apptCfg?.enabled && apptCfg?.has_windows && !isApptOnly && (
                  <div
                    className="mb-4 grid grid-cols-2 gap-2 p-1 bg-white/70 rounded-xl border border-slate-200 text-slate-800"
                    data-testid="landing-mode-switcher"
                  >
                    <button
                      type="button"
                      onClick={() => setMode('info')}
                      className={`px-3 py-2.5 rounded-lg text-sm font-semibold transition ${
                        mode === 'info'
                          ? 'bg-slate-900 text-white shadow'
                          : 'text-slate-700 hover:bg-white'
                      }`}
                      data-testid="landing-mode-info"
                    >
                      {language === 'es' ? 'Contáctame' : 'Get in touch'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode('appointment')}
                      className={`px-3 py-2.5 rounded-lg text-sm font-semibold transition ${
                        mode === 'appointment'
                          ? 'bg-slate-900 text-white shadow'
                          : 'text-slate-700 hover:bg-white'
                      }`}
                      data-testid="landing-mode-appointment"
                    >
                      {language === 'es' ? 'Agendar llamada' : 'Schedule a call'}
                    </button>
                  </div>
                )}

                {mode === 'info' && !isApptOnly && (
              <form onSubmit={submit} noValidate
                className={`rounded-2xl p-6 sm:p-8 border ${tokens.card} space-y-5 text-slate-800`}
                data-testid="landing-form">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold">
                    {language === 'es' ? 'Cuéntame cómo puedo ayudarte' : 'Tell Me How I Can Help You'}
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {language === 'es'
                      ? 'Completa el formulario y te contactaré para conversar sobre tus opciones.'
                      : "Complete the form below and I'll contact you to discuss your options."}
                  </p>
                </div>

                {!isSimple && enabledServices.length > 1 && (
                  <div>
                    <div className="text-sm font-semibold text-slate-800 mb-2">
                      {language === 'es'
                        ? <>¿En qué podemos ayudarte? <span className="font-normal text-slate-500">(Selecciona todas las que apliquen)</span></>
                        : <>What can we help you with? <span className="font-normal text-slate-500">(Select all that apply)</span></>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {enabledServices.map((s) => {
                        const Icon = SERVICE_ICONS[s.key] || Shield;
                        const active = form.services_interested.includes(s.key);
                        return (
                          <button type="button" key={s.key}
                            onClick={() => toggleService(s.key)}
                            className={`flex items-center gap-2.5 border rounded-lg px-3 py-2.5 text-left transition ${active
                              ? 'bg-slate-900 text-white border-slate-900'
                              : 'bg-white border-slate-300 hover:border-slate-500'}`}
                            data-testid={`landing-service-${s.key}`}>
                            <Icon size={16} className={active ? 'text-white' : tokens.accent} />
                            <span className="text-sm font-medium">{s.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="text-sm font-semibold text-slate-800">
                    {language === 'es' ? 'Tu información' : 'Your Information'}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label={`${t('form_first_name')} *`} testId="landing-first-name" error={errors.first_name}>
                      <input className={inputClass(errors.first_name)}
                        aria-invalid={!!errors.first_name}
                        value={form.first_name}
                        onChange={(e) => updateField('first_name', e.target.value)} />
                    </Field>
                    <Field label={`${t('form_last_name')} *`} testId="landing-last-name" error={errors.last_name}>
                      <input className={inputClass(errors.last_name)}
                        aria-invalid={!!errors.last_name}
                        value={form.last_name}
                        onChange={(e) => updateField('last_name', e.target.value)} />
                    </Field>
                  </div>

                  <Field label={`${t('form_phone')} *`} testId="landing-phone" error={errors.phone}>
                    <input type="tel" className={inputClass(errors.phone)}
                      aria-invalid={!!errors.phone}
                      value={formatPhoneInput(form.phone)}
                      onChange={(e) => updateField('phone', e.target.value)} />
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {cfg.email !== 'hidden' && (
                      <Field label={`Email Address${cfg.email === 'required' ? ' *' : ''}`} testId="landing-email" error={errors.email}>
                        <input type="email"
                          className={inputClass(errors.email)}
                          aria-invalid={!!errors.email}
                          value={form.email}
                          onChange={(e) => updateField('email', e.target.value)} />
                      </Field>
                    )}
                    {cfg.zip !== 'hidden' && (
                      <Field label={`ZIP Code${cfg.zip === 'required' ? ' *' : ''}`} testId="landing-zip-code" error={errors.zip_code}>
                        <input inputMode="numeric" maxLength={10}
                          className={inputClass(errors.zip_code)}
                          aria-invalid={!!errors.zip_code}
                          value={form.zip_code}
                          onChange={(e) => updateField('zip_code', e.target.value)} />
                      </Field>
                    )}
                  </div>

                  {cfg.preferred_contact !== 'hidden' && (
                    <Field label={t('form_preferred_contact')} testId="landing-preferred-contact" error={errors.preferred_contact}>
                      <select className={inputClass(errors.preferred_contact)}
                        aria-invalid={!!errors.preferred_contact}
                        value={form.preferred_contact}
                        onChange={(e) => updateField('preferred_contact', e.target.value)}>
                        <option value="">Select an option</option>
                        <option value="phone">Phone</option>
                        <option value="text">Text</option>
                        <option value="email">Email</option>
                      </select>
                    </Field>
                  )}

                  {cfg.message !== 'hidden' && (
                    <Field label={t('form_message')} testId="landing-message" error={errors.message}>
                      <textarea rows={3}
                        className={inputClass(errors.message)}
                        placeholder={t('form_message_placeholder')}
                        value={form.message}
                        onChange={(e) => updateField('message', e.target.value)} />
                    </Field>
                  )}
                </div>

                {/* Consent */}
                <div data-testid="landing-consent-accepted">
                  <label className="flex items-start gap-3 text-xs cursor-pointer" data-testid="landing-consent">
                    <input
                      type="checkbox"
                      className={`mt-1 accent-slate-900 ${errors.consent_accepted ? 'ring-2 ring-red-500 rounded' : ''}`}
                      aria-invalid={!!errors.consent_accepted}
                      checked={form.consent_accepted}
                      onChange={(e) => {
                        setForm((f) => ({ ...f, consent_accepted: e.target.checked }));
                        if (errors.consent_accepted) {
                          setErrors((prev) => ({ ...prev, consent_accepted: undefined }));
                        }
                      }}
                    />
                    <span className="text-slate-600 leading-relaxed">
                      {consentText}
                    </span>
                  </label>
                  {errors.consent_accepted && (
                    <p className="mt-1.5 ml-6 text-xs text-red-600" data-testid="landing-consent-accepted-error">
                      {errors.consent_accepted}
                    </p>
                  )}
                </div>

                {/* SMS opt-in — Phase 27.37.C. SEPARATE from the general
                    consent, defaults UNCHECKED, and is NOT required to
                    submit the form. A2P compliance. */}
                <div data-testid="landing-sms-consent">
                  <label className="flex items-start gap-3 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 accent-slate-900"
                      checked={form.sms_consent_accepted}
                      onChange={(e) => setForm((f) => ({ ...f, sms_consent_accepted: e.target.checked }))}
                      data-testid="landing-sms-consent-checkbox"
                    />
                    <span className="text-slate-600 leading-relaxed">
                      I agree to receive SMS/MMS messages from KR Enterprise
                      Group and its authorized representatives regarding my
                      inquiry, requested insurance information, appointments,
                      application or service updates, and customer support.
                      Message frequency varies. Message and data rates may
                      apply. Reply <strong>STOP</strong> to opt out or{' '}
                      <strong>HELP</strong> for assistance. Consent is not a
                      condition of purchase.
                      <span className="block mt-1 text-[10px] text-slate-500">
                        <a href="https://krenterprisegroup.app/privacy" target="_blank" rel="noreferrer" className="underline hover:text-slate-800">Privacy Policy</a>
                        {' · '}
                        <a href="https://krenterprisegroup.app/terms" target="_blank" rel="noreferrer" className="underline hover:text-slate-800">Terms &amp; Conditions</a>
                      </span>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full rounded-xl px-6 py-3.5 font-semibold uppercase tracking-wide transition disabled:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${tokens.accentBg}`}
                  data-testid="landing-submit"
                >
                  {submitting ? <><Loader2 className="inline h-4 w-4 mr-2 animate-spin" /> {submittingLabel}</> : ctaLabel}
                </button>

                <p className="text-xs text-slate-500 inline-flex items-center gap-1 justify-center w-full">
                  <Lock size={12} />{' '}
                  {language === 'es'
                    ? 'Tu información es segura y nunca será compartida.'
                    : 'Your information is secure and will never be shared.'}
                </p>
              </form>
                )}

                {mode === 'appointment' && apptCfg?.enabled && apptCfg?.has_windows && (
                  <AppointmentBooking
                    slug={slug}
                    tokens={tokens}
                    agentName={page.agent_display?.display_name}
                    enabledServices={enabledServices}
                    language={language}
                    startExpanded
                  />
                )}
                {isApptOnly && !(apptCfg?.enabled && apptCfg?.has_windows) && (
                  <div className={`rounded-2xl p-8 text-center border ${tokens.card} text-slate-700`} data-testid="landing-appt-unavailable">
                    <p className="font-semibold">
                      {language === 'es' ? 'Agendamiento no disponible aún' : 'Booking not available yet'}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      {language === 'es'
                        ? 'Este agente todavía no ha configurado su disponibilidad. Vuelve más tarde.'
                        : 'This agent has not set up availability yet. Please check back soon.'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Trust highlights */}
      {!isSimple && (
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 grid grid-cols-1 sm:grid-cols-3 gap-8">
          {TRUST_HIGHLIGHTS.map((h) => {
            const Icon = h.icon;
            return (
              <div key={h.title} className="text-center">
                <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center border-2 ${tokens.card}`}>
                  <Icon size={22} className={tokens.accent} />
                </div>
                <div className="mt-3 font-bold text-slate-900">{h.title}</div>
                <p className="mt-2 text-sm text-slate-600 max-w-[240px] mx-auto leading-relaxed">{h.body}</p>
              </div>
            );
          })}
        </div>
      </section>
      )}
      {/* Footer trust bar + info */}
      <footer className={`${tokens.heroBg} ${tokens.heroText}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 pb-8 border-b border-white/10">
            {FOOTER_BADGES.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="flex gap-3">
                  <Icon size={20} className={`${tokens.accent} flex-shrink-0 mt-0.5`} />
                  <div>
                    <div className="font-semibold text-sm leading-tight">{b.title}</div>
                    <div className="text-xs opacity-70 mt-1">{b.body}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pt-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
            {brand.show && (
              <div>
                <div className="flex items-center gap-2">
                  {brand.logo_url ? (
                    <img
                      src={absoluteUrl(brand.logo_url)}
                      alt={`${brand.name || 'Agency'} logo`}
                      className="w-8 h-8 rounded object-contain bg-white/10"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center font-black text-xs">KR</div>
                  )}
                  <div className="font-bold">{brand.name}</div>
                </div>
                <p className="mt-3 text-xs opacity-70 max-w-xs">
                  Helping individuals and families protect their health, their future, and the ones they love.
                </p>
              </div>
            )}
            <div>
              <div className="uppercase text-[10px] tracking-widest opacity-70 mb-2">Contact</div>
              <div className="space-y-1.5 text-xs">
                {page.agent_display?.phone && (
                  <a href={`tel:${page.agent_display.phone}`} className="flex items-center gap-2 hover:underline">
                    <Phone size={12} /> {page.agent_display.phone}
                  </a>
                )}
                {page.agent_display?.email && (
                  <a href={`mailto:${page.agent_display.email}`} className="flex items-center gap-2 hover:underline break-all">
                    <Mail size={12} /> {page.agent_display.email}
                  </a>
                )}
              </div>
            </div>
            <div>
              <div className="uppercase text-[10px] tracking-widest opacity-70 mb-2">Legal</div>
              <div className="space-y-1.5 text-xs">
                <a href="/privacy" className="block hover:underline opacity-80">Privacy Notice</a>
                <a href="/terms" className="block hover:underline opacity-80">Terms</a>
                <a href="/refund-policy" className="block hover:underline opacity-80">Refund &amp; Return Policy</a>
              </div>
            </div>
          </div>

          <div className="pt-6 mt-6 border-t border-white/10 text-[10px] opacity-60 text-center leading-relaxed">
            Not affiliated with or endorsed by the U.S. government or the federal Medicare program.
            We do not offer every plan available in your area. For information on all your options,
            contact Medicare.gov or 1-800-MEDICARE.
            <br />
            © {new Date().getFullYear()} {brand.name || 'KR Enterprise Group'}. All rights reserved.
            {brand.powered_by_kr && (
              <>
                {' · '}
                <span data-testid="landing-powered-by-kr">
                  Powered by <span className="font-semibold">KR Enterprise Group</span>
                </span>
              </>
            )}
          </div>
        </div>
      </footer>
      <PublicChatWidget
        slug={slug}
        language={language}
        accentColor={(page?.branding?.primary_color) || '#0f2a5e'}
        onQualified={(fields, sessionId, meta = {}) => {
          setChatSessionId(sessionId);
          if (meta.autoCreated && meta.leadId) {
            setChatAutoCreatedLead({ leadId: meta.leadId, fields });
            // Also mark `submitted` so any success card downstream
            // renders the same "we got you" state.
            setSubmitted(true);
            return;
          }
          setForm((f) => {
            const next = { ...f };
            if (fields.first_name && !f.first_name) next.first_name = fields.first_name;
            if (fields.phone && !f.phone) next.phone = fields.phone;
            if (fields.email && !f.email) next.email = fields.email;
            const aoi = (fields.area_of_interest || '').toLowerCase();
            const map = {
              medicare: 'medicare',
              aca: 'aca',
              life: 'life',
              dental: 'dental_vision',
              vision: 'dental_vision',
              supplemental: 'supplemental',
              supplement: 'supplemental',
            };
            for (const [k, v] of Object.entries(map)) {
              if (aoi.includes(k) && !(f.services_interested || []).includes(v)) {
                next.services_interested = [...(f.services_interested || []), v];
                break;
              }
            }
            return next;
          });
          try {
            document.querySelector('form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch { /* older browsers */ }
        }}
      />
    </div>
  );
}

function Field({ label, children, testId, error }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="block text-sm font-medium text-slate-700 mb-1.5">{label}</span>
      {children}
      {error && (
        <p className="mt-1.5 text-xs text-red-600" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </label>
  );
}


// Circular agent avatar with graceful fallback to initials when the
// photo URL is missing OR fails to load (404 / auth-gated / stale).
// Prevents the browser's broken-image icon on landing pages.
function RosterAvatar({ url, name, sizeClass }) {
  const [failed, setFailed] = useState(false);
  const initials = (name || '?').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const abs = absoluteUrl(url);
  if (!abs || failed) {
    return (
      <div className={`${sizeClass} rounded-full bg-slate-200 grid place-items-center font-bold text-slate-600`}>
        {initials}
      </div>
    );
  }
  return (
    <img
      src={abs}
      alt={name}
      onError={() => setFailed(true)}
      className={`${sizeClass} rounded-full object-cover border border-slate-200`}
    />
  );
}

// ── Team scheduler (KR roster) ─────────────────────────────────────
// KR-owned page: the visitor sees each agent (photo/name/blurb/contact)
// and picks an action per agent — "Request information" (a lead that
// goes straight to that agent) and/or "Book appointment" (that agent's
// own availability). Which buttons show is controlled per agent in the
// editor, so one page adapts to whatever this campaign/event needs.
function TeamRosterView({ slug, tokens, content, brand, page, language }) {
  const [agents, setAgents] = useState(null);
  const [selected, setSelected] = useState(null); // { agent, mode: 'book' | 'request' }

  useEffect(() => {
    let mounted = true;
    publicLandingPagesAPI.getTeamRoster(slug)
      .then((r) => { if (mounted) setAgents(r.data?.agents || []); })
      .catch(() => { if (mounted) setAgents([]); });
    return () => { mounted = false; };
  }, [slug]);

  const enabledServices = SERVICE_CATALOG.filter((s) => (page.services_enabled || []).includes(s.key));

  const heading = (content.headline || '').trim()
    || (language === 'es' ? 'Conoce a nuestro equipo' : 'Meet our team');
  const sub = (content.supporting_text || '').trim()
    || (language === 'es'
      ? 'Elige un agente para pedir información o agendar una cita.'
      : 'Choose an agent to request info or book an appointment.');

  return (
    <div className={`min-h-screen ${tokens.body} ${tokens.fontScale}`} data-testid="public-team-page">
      <section className={`${tokens.heroBg} ${tokens.heroText}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 lg:py-14 text-center">
          {brand?.show && brand?.name && (
            <p className="text-sm font-semibold opacity-80 mb-3">{brand.name}</p>
          )}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold">{heading}</h1>
          <p className="mt-3 text-base opacity-90 max-w-2xl mx-auto">{sub}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
        {selected ? (
          <div>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="mb-5 text-sm font-semibold text-slate-600 hover:text-slate-900"
              data-testid="team-back-btn"
            >
              {language === 'es' ? '\u2190 Elegir otro agente' : '\u2190 Choose another agent'}
            </button>
            <div className="mb-4 flex items-center gap-3">
              <RosterAvatar url={selected.agent.photo_url} name={selected.agent.name} sizeClass="h-14 w-14" />
              <div>
                <p className="font-bold text-slate-900 text-lg">{selected.agent.name}</p>
                {selected.agent.blurb && <p className="text-sm text-slate-500">{selected.agent.blurb}</p>}
              </div>
            </div>
            {selected.mode === 'book' ? (
              <AppointmentBooking
                slug={selected.agent.booking_mode === 'delegate' ? selected.agent.book_slug : slug}
                teamNpn={selected.agent.booking_mode === 'delegate' ? null : selected.agent.npn}
                tokens={tokens}
                agentName={selected.agent.name}
                enabledServices={enabledServices}
                language={language}
                startExpanded
              />
            ) : (
              <RequestInfoForm
                slug={slug}
                agent={selected.agent}
                tokens={tokens}
                page={page}
                enabledServices={enabledServices}
                language={language}
              />
            )}
          </div>
        ) : agents === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>
        ) : agents.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="team-empty">
            {language === 'es' ? 'No hay agentes disponibles en este momento.' : 'No agents are available right now.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="team-roster-grid">
            {agents.map((a) => {
              const showReq = a.allow_request_info !== false;
              const showBook = a.allow_booking !== false;
              return (
                <div
                  key={a.npn}
                  className={`rounded-2xl border ${tokens.card} p-5 text-center flex flex-col items-center`}
                  data-testid={`team-agent-${a.npn}`}
                >
                  <RosterAvatar url={a.photo_url} name={a.name} sizeClass="h-20 w-20" />
                  <p className="mt-3 font-bold text-slate-900">{a.name}</p>
                  {a.blurb && <p className="text-sm text-slate-500 mt-1">{a.blurb}</p>}
                  <div className="mt-2 space-y-0.5 text-xs text-slate-500">
                    {a.phone && (
                      <a href={`tel:${a.phone}`} className="flex items-center gap-1.5 justify-center hover:text-slate-800">
                        <Phone size={12} /> {a.phone}
                      </a>
                    )}
                    {a.email && (
                      <a href={`mailto:${a.email}`} className="flex items-center gap-1.5 justify-center hover:text-slate-800 break-all">
                        <Mail size={12} /> {a.email}
                      </a>
                    )}
                  </div>
                  <div className="mt-4 w-full space-y-2">
                    {showReq && (
                      <button
                        type="button"
                        onClick={() => setSelected({ agent: a, mode: 'request' })}
                        className={`w-full rounded-lg border px-4 py-2.5 text-sm font-semibold transition ${tokens.card} hover:bg-slate-50 text-slate-800 border-slate-300`}
                        data-testid={`team-request-${a.npn}`}
                      >
                        {language === 'es' ? 'Solicitar información' : 'Request information'}
                      </button>
                    )}
                    {showBook && (
                      <button
                        type="button"
                        disabled={!a.bookable}
                        onClick={() => setSelected({ agent: a, mode: 'book' })}
                        className="w-full rounded-lg bg-slate-900 text-white px-4 py-2.5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`team-book-${a.npn}`}
                      >
                        {a.bookable
                          ? (language === 'es' ? 'Agendar cita' : 'Book appointment')
                          : (language === 'es' ? 'Citas no disponibles' : 'Booking unavailable')}
                      </button>
                    )}
                    {!showReq && !showBook && (
                      <p className="text-xs italic text-slate-400">
                        {language === 'es' ? 'Sin acciones disponibles' : 'No actions available'}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

// Lead-capture form for a specific team agent. Submits straight into
// the Leads pipeline assigned to THAT agent (agent_npn). Bilingual.
function RequestInfoForm({ slug, agent, tokens, page, enabledServices = [], language = 'en' }) {
  const L = (en, es) => (language === 'es' ? es : en);
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '', email: '', message: '',
    services_interested: [], consent_accepted: false, sms_consent_accepted: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const consentText = (language === 'es' && (page.consent?.text_es || '').trim())
    ? page.consent.text_es : page.consent?.text;

  const upd = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((p) => (p[k] ? { ...p, [k]: undefined } : p)); };
  const toggleSvc = (key) => setForm((f) => ({
    ...f,
    services_interested: f.services_interested.includes(key)
      ? f.services_interested.filter((s) => s !== key)
      : [...f.services_interested, key],
  }));

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.first_name.trim()) next.first_name = L('Required', 'Requerido');
    if (!form.last_name.trim()) next.last_name = L('Required', 'Requerido');
    if (!form.phone.trim()) next.phone = L('Required', 'Requerido');
    else if (form.phone.replace(/\D/g, '').length < 7) next.phone = L('Invalid phone', 'Teléfono inválido');
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = L('Invalid email', 'Correo inválido');
    if (!form.consent_accepted) next.consent_accepted = L('Please accept to continue', 'Acepta para continuar');
    if (Object.keys(next).length) { setErrors(next); return; }
    setSubmitting(true);
    try {
      const usp = new URLSearchParams(window.location.search);
      const utm = {};
      ['source', 'medium', 'campaign', 'content', 'term'].forEach((k) => {
        const v = usp.get(`utm_${k}`); if (v) utm[`utm_${k}`] = v;
      });
      await publicLandingPagesAPI.submit(slug, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        message: form.message.trim() || undefined,
        services_interested: form.services_interested,
        consent_accepted: form.consent_accepted,
        sms_consent_accepted: form.sms_consent_accepted,
        sms_consent_language: language,
        preferred_language: language,
        agent_npn: agent.npn,
        ...utm,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || L('Unable to submit — please try again.', 'No se pudo enviar — inténtalo de nuevo.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className={`rounded-2xl p-8 text-center border ${tokens.card} text-slate-800`} data-testid="team-request-success">
        <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500" />
        <h2 className="mt-4 text-2xl font-bold">{L('Thank you', 'Gracias')}</h2>
        <p className="mt-2 text-slate-600">
          {L(`${agent.name} will contact you shortly.`, `${agent.name} te contactará en breve.`)}
        </p>
      </div>
    );
  }

  const inputCls = (err) => `w-full rounded-lg border px-3 py-2.5 outline-none transition ${err ? 'border-red-500 bg-red-50/30' : tokens.input}`;

  return (
    <form onSubmit={submit} noValidate className={`rounded-2xl p-6 sm:p-8 border ${tokens.card} space-y-4 text-slate-800`} data-testid="team-request-form">
      <h2 className="text-xl font-bold">{L('Request information', 'Solicitar información')}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={`${L('First Name', 'Nombre')} *`} testId="team-req-first-name" error={errors.first_name}>
          <input className={inputCls(errors.first_name)} value={form.first_name} onChange={(e) => upd('first_name', e.target.value)} />
        </Field>
        <Field label={`${L('Last Name', 'Apellido')} *`} testId="team-req-last-name" error={errors.last_name}>
          <input className={inputCls(errors.last_name)} value={form.last_name} onChange={(e) => upd('last_name', e.target.value)} />
        </Field>
      </div>
      <Field label={`${L('Phone', 'Teléfono')} *`} testId="team-req-phone" error={errors.phone}>
        <input type="tel" className={inputCls(errors.phone)} value={formatPhoneInput(form.phone)} onChange={(e) => upd('phone', e.target.value)} />
      </Field>
      <Field label={L('Email', 'Correo electrónico')} testId="team-req-email" error={errors.email}>
        <input type="email" className={inputCls(errors.email)} value={form.email} onChange={(e) => upd('email', e.target.value)} />
      </Field>
      {enabledServices.length > 1 && (
        <div>
          <div className="text-sm font-semibold text-slate-800 mb-2">
            {L('What can we help you with?', '¿En qué podemos ayudarte?')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {enabledServices.map((s) => {
              const Icon = SERVICE_ICONS[s.key] || Shield;
              const active = form.services_interested.includes(s.key);
              return (
                <button type="button" key={s.key} onClick={() => toggleSvc(s.key)}
                  className={`flex items-center gap-2.5 border rounded-lg px-3 py-2.5 text-left transition ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-300 hover:border-slate-500'}`}
                  data-testid={`team-req-service-${s.key}`}>
                  <Icon size={16} className={active ? 'text-white' : tokens.accent} />
                  <span className="text-sm font-medium">{serviceLabel(s, language)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <Field label={L('How can we help? (Optional)', '¿Cómo podemos ayudarte? (Opcional)')} testId="team-req-message">
        <textarea rows={3} className={inputCls(false)} value={form.message} onChange={(e) => upd('message', e.target.value)} />
      </Field>
      <div data-testid="team-req-consent">
        <label className="flex items-start gap-3 text-xs cursor-pointer">
          <input type="checkbox" className="mt-1 accent-slate-900" checked={form.consent_accepted}
            onChange={(e) => upd('consent_accepted', e.target.checked)} data-testid="team-req-consent-checkbox" />
          <span className="text-slate-600 leading-relaxed">{consentText}</span>
        </label>
        {errors.consent_accepted && <p className="mt-1.5 ml-6 text-xs text-red-600">{errors.consent_accepted}</p>}
      </div>
      <button type="submit" disabled={submitting}
        className={`w-full rounded-xl px-6 py-3.5 font-semibold uppercase tracking-wide transition disabled:opacity-70 ${tokens.accentBg}`}
        data-testid="team-req-submit">
        {submitting ? <><Loader2 className="inline h-4 w-4 mr-2 animate-spin" /> {L('Submitting…', 'Enviando…')}</> : L('Send my info', 'Enviar mi información')}
      </button>
    </form>
  );
}


// ── Appointment booking widget ────────────────────────────────────
// Renders only when the landing page has `appointments_config.enabled=true`.
// Kept self-contained so it doesn't complicate the main form state
// machine — its own local state, its own submit path.
function AppointmentBooking({ slug, tokens, agentName, enabledServices = [], startExpanded = false, language = 'en', teamNpn = null }) {
  const L = (en, es) => (language === 'es' ? es : en);
  const cfgCall = () => (teamNpn
    ? publicLandingPagesAPI.getTeamMemberConfig(slug, teamNpn)
    : publicLandingPagesAPI.getAppointmentConfig(slug));
  const slotsCall = (d) => (teamNpn
    ? publicLandingPagesAPI.getTeamMemberSlots(slug, teamNpn, d)
    : publicLandingPagesAPI.getAppointmentSlots(slug, d));
  const bookCall = (p) => (teamNpn
    ? publicLandingPagesAPI.bookTeamMemberAppointment(slug, teamNpn, p)
    : publicLandingPagesAPI.bookAppointment(slug, p));
  const [cfg, setCfg] = useState(null);
  const [expanded, setExpanded] = useState(startExpanded);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '', email: '', notes: '',
    address: '', city: '', state: '', zip_code: '',
    services_interested: [], consent_accepted: true,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await cfgCall();
        if (mounted) setCfg(r.data);
      } catch (e) {
        if (mounted) setCfg({ enabled: false });
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  // Default the date to tomorrow (respecting the agent's timezone
  // is handled server-side; a local "tomorrow" is a safe default).
  useEffect(() => {
    if (!cfg?.enabled || date) return;
    const d = new Date();
    d.setDate(d.getDate() + 1);
    setDate(d.toISOString().slice(0, 10));
  }, [cfg, date]);

  useEffect(() => {
    if (!cfg?.enabled || !date || !expanded) return;
    let mounted = true;
    setLoadingSlots(true);
    setSelectedSlot('');
    (async () => {
      try {
        const r = await slotsCall(date);
        if (mounted) setSlots(r.data.slots || []);
      } catch (e) {
        if (mounted) setSlots([]);
      } finally {
        if (mounted) setLoadingSlots(false);
      }
    })();
    return () => { mounted = false; };
  }, [cfg, date, expanded, slug]);

  if (!cfg?.enabled) return null;
  if (!cfg.has_windows) return null;

  const updateField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.first_name.trim()) next.first_name = L('Required', 'Requerido');
    if (!form.last_name.trim()) next.last_name = L('Required', 'Requerido');
    if (!form.phone.trim()) next.phone = L('Required', 'Requerido');
    else if (form.phone.replace(/\D/g, '').length < 7) next.phone = L('Invalid phone', 'Teléfono inválido');
    // Email is REQUIRED for bookings — the confirmation email
    // (with .ics) is the only channel we have to give the visitor
    // proof of the booking, so we won't accept a booking without one.
    if (!form.email.trim()) {
      next.email = L('Email is required for the confirmation', 'El correo es obligatorio para la confirmación');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = L('Invalid email', 'Correo inválido');
    }
    if (!selectedSlot) next.slot = L('Please pick a time', 'Elige un horario');
    if (Object.keys(next).length) { setErrors(next); return; }
    setSubmitting(true);
    try {
      const utm = {};
      const usp = new URLSearchParams(window.location.search);
      ['source', 'medium', 'campaign', 'content', 'term'].forEach((k) => {
        const v = usp.get(`utm_${k}`);
        if (v) utm[`utm_${k}`] = v;
      });
      const r = await bookCall({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),  // required — validated above
        notes: form.notes.trim() || undefined,
        // Phase 27.47 — Optional address collected via Google autocomplete
        // so the agent knows where the visit will happen.
        address: form.address?.trim() || undefined,
        city: form.city?.trim() || undefined,
        state: form.state?.trim() || undefined,
        zip_code: form.zip_code?.trim() || undefined,
        scheduled_at: selectedSlot,
        services_interested: form.services_interested,
        consent_accepted: true,
        ...utm,
      });
      setBooked(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 409) {
        // Someone else grabbed the slot — refresh the list.
        toast.error(detail || L('That time was just taken — please pick another.', 'Ese horario acaba de ser tomado — elige otro.'));
        setSelectedSlot('');
        const r = await slotsCall(date).catch(() => ({ data: { slots: [] } }));
        setSlots(r.data.slots || []);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error(L('Unable to book — please try again.', 'No se pudo reservar — inténtalo de nuevo.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Compute a nice local time label from the ISO UTC slot.
  const slotLabel = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: cfg.timezone });
    } catch (_) {
      return iso;
    }
  };

  // Min/max date for the picker.
  const today = new Date().toISOString().slice(0, 10);
  const maxDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + (cfg.max_days_ahead || 30));
    return d.toISOString().slice(0, 10);
  })();

  return (
    <div
      className={`mt-4 rounded-2xl p-6 sm:p-8 border ${tokens.card} text-slate-800`}
      data-testid="landing-appointment-widget"
    >
      {booked ? (
        <div className="text-center" data-testid="landing-appointment-confirmed">
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
          <h3 className="mt-3 text-lg font-bold">{L('Your appointment is booked', 'Tu cita está reservada')}</h3>
          <p className="mt-1 text-sm text-slate-600">
            {agentName || L('The agent', 'El agente')}{' '}
            {L('will meet you on', 'se reunirá contigo el')}{' '}
            <b>{new Date(booked.scheduled_at).toLocaleString(language === 'es' ? 'es' : [], {
              weekday: 'long', month: 'long', day: 'numeric',
              hour: 'numeric', minute: '2-digit', timeZone: cfg.timezone,
            })}</b>.
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {L('A confirmation with a calendar invite (.ics) has been sent to your email — check your inbox in a minute.',
               'Se envió una confirmación con una invitación de calendario (.ics) a tu correo — revisa tu bandeja en un minuto.')}
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                {L('Or book directly', 'O reserva directamente')}
              </div>
              <h3 className="mt-1 text-lg sm:text-xl font-bold">
                {L(`Schedule a ${cfg.duration_minutes}-minute call`, `Agenda una llamada de ${cfg.duration_minutes} minutos`)}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                {L("Pick a time that works for you — I'll be there.", 'Elige el horario que mejor te convenga — ahí estaré.')}
              </p>
            </div>
            {!expanded && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className={`px-4 py-2 rounded-lg font-semibold text-sm ${tokens.accentBg}`}
                data-testid="landing-appointment-toggle"
              >
                {L('Book time →', 'Reservar horario →')}
              </button>
            )}
          </div>

          {expanded && (
            <form onSubmit={submit} noValidate className="mt-5 space-y-4">
              <Field label={L('Choose a date', 'Elige una fecha')} testId="landing-appt-date">
                <input
                  type="date"
                  min={today}
                  max={maxDate}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2.5 outline-none transition ${tokens.input}`}
                />
              </Field>
              {loadingSlots ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 size={16} className="animate-spin" /> {L('Loading available times…', 'Cargando horarios disponibles…')}
                </div>
              ) : slots.length === 0 ? (
                <div className="text-sm text-slate-500 italic py-4 text-center border border-dashed border-slate-300 rounded-lg">
                  {L('No available times on this day. Try another date.', 'No hay horarios disponibles este día. Prueba otra fecha.')}
                </div>
              ) : (
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-2">
                    {L(`Available times (${slots.length})`, `Horarios disponibles (${slots.length})`)}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto pr-1">
                    {slots.map((s) => (
                      <button
                        key={s} type="button"
                        onClick={() => { setSelectedSlot(s); setErrors((p) => ({ ...p, slot: undefined })); }}
                        className={`text-xs sm:text-sm rounded-md border px-2 py-2 transition ${
                          selectedSlot === s
                            ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white border-slate-300 hover:border-slate-500'
                        }`}
                        data-testid={`landing-appt-slot-${s}`}
                      >
                        {slotLabel(s)}
                      </button>
                    ))}
                  </div>
                  {errors.slot && (
                    <p className="mt-2 text-xs text-red-600">{errors.slot}</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-200">
                <Field label={`${L('First Name', 'Nombre')} *`} testId="landing-appt-first-name" error={errors.first_name}>
                  <input
                    className={`w-full rounded-lg border px-3 py-2.5 outline-none transition ${
                      errors.first_name ? 'border-red-500 bg-red-50/30' : tokens.input
                    }`}
                    value={form.first_name}
                    onChange={(e) => updateField('first_name', e.target.value)}
                  />
                </Field>
                <Field label={`${L('Last Name', 'Apellido')} *`} testId="landing-appt-last-name" error={errors.last_name}>
                  <input
                    className={`w-full rounded-lg border px-3 py-2.5 outline-none transition ${
                      errors.last_name ? 'border-red-500 bg-red-50/30' : tokens.input
                    }`}
                    value={form.last_name}
                    onChange={(e) => updateField('last_name', e.target.value)}
                  />
                </Field>
              </div>
              <Field label={`${L('Phone', 'Teléfono')} *`} testId="landing-appt-phone" error={errors.phone}>
                <input type="tel"
                  className={`w-full rounded-lg border px-3 py-2.5 outline-none transition ${
                    errors.phone ? 'border-red-500 bg-red-50/30' : tokens.input
                  }`}
                  value={form.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                />
              </Field>
              <Field label={`${L('Email', 'Correo electrónico')} *`} testId="landing-appt-email" error={errors.email}>
                <input type="email"
                  className={`w-full rounded-lg border px-3 py-2.5 outline-none transition ${
                    errors.email ? 'border-red-500 bg-red-50/30' : tokens.input
                  }`}
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  {L("We'll send your confirmation and calendar invite here.", 'Aquí enviaremos tu confirmación e invitación de calendario.')}
                </p>
              </Field>

              {/* Phase 27.47 — Address (Google autocomplete). Optional
                  but shown by default because a booked visit needs a
                  location; leaving it empty is fine when the visit is
                  virtual / by phone. */}
              <Field label={L('Address (for the visit)', 'Dirección (para la visita)')} testId="landing-appt-address">
                <AddressAutocomplete
                  value={form.address}
                  onChange={(v) => updateField('address', v)}
                  onPlaceSelected={(parsed) => {
                    setForm((f) => ({
                      ...f,
                      address: parsed.street || parsed.formatted,
                      city: parsed.city || f.city,
                      state: parsed.state || f.state,
                      zip_code: parsed.zip || f.zip_code,
                    }));
                    flashAutofilled(['landing-appt-address-input']);
                  }}
                  placeholder={L('Start typing your address…', 'Empieza a escribir tu dirección…')}
                  className="w-full"
                  testId="landing-appt-address-input"
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  {L('Optional — leave blank if the appointment is by phone or video.', 'Opcional — déjalo en blanco si la cita es por teléfono o video.')}
                </p>
              </Field>

              {enabledServices.length > 1 && (
                <div>
                  <div className="text-sm font-semibold text-slate-800 mb-2">
                    {language === 'es'
                      ? <>¿En qué podemos ayudarte? <span className="font-normal text-slate-500">(Selecciona todas las que apliquen)</span></>
                      : <>What can we help you with? <span className="font-normal text-slate-500">(Select all that apply)</span></>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {enabledServices.map((s) => {
                      const Icon = SERVICE_ICONS[s.key] || Shield;
                      const active = form.services_interested.includes(s.key);
                      return (
                        <button type="button" key={s.key}
                          onClick={() => setForm((f) => ({
                            ...f,
                            services_interested: f.services_interested.includes(s.key)
                              ? f.services_interested.filter((k) => k !== s.key)
                              : [...f.services_interested, s.key],
                          }))}
                          className={`flex items-center gap-2.5 border rounded-lg px-3 py-2.5 text-left transition ${active
                            ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white border-slate-300 hover:border-slate-500'}`}
                          data-testid={`landing-appt-service-${s.key}`}>
                          <Icon size={16} className={active ? 'text-white' : tokens.accent} />
                          <span className="text-sm font-medium">{serviceLabel(s, language)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <Field label={L('What would you like to discuss? (Optional)', '¿De qué te gustaría hablar? (Opcional)')} testId="landing-appt-notes">
                <textarea rows={2}
                  className={`w-full rounded-lg border px-3 py-2.5 outline-none transition ${tokens.input}`}
                  value={form.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                />
              </Field>

              <button
                type="submit" disabled={submitting}
                className={`w-full rounded-lg py-3 font-semibold text-sm sm:text-base ${tokens.accentBg} disabled:opacity-60`}
                data-testid="landing-appt-submit"
              >
                {submitting
                  ? <><Loader2 className="inline h-4 w-4 mr-2 animate-spin" /> {L('Booking…', 'Reservando…')}</>
                  : L('Confirm Appointment', 'Confirmar cita')}
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
}
